import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Calculator, TrendingUp, FileText, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function OnboardingModal({ isOpen, onClose, userName }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);

  if (!isOpen) return null;

  const steps = [
    {
      title: `Willkommen bei ivoliq, ${userName || 'Freelancer'}! 👋`,
      description: 'Lass uns dein Finanz-Cockpit in 30 Sekunden einrichten.',
      icon: Calculator,
      features: [
        'Automatische Steuerberechnung für DE, AT & CH',
        'Einnahmen & Ausgaben im Überblick',
        'Rechnungen in Sekunden erstellen'
      ]
    },
    {
      title: 'Dein Dashboard ist bereit! 🚀',
      description: 'Du kannst jetzt loslegen:',
      icon: TrendingUp,
      features: [
        'Füge deinen ersten Kunden hinzu',
        'Starte die Zeiterfassung',
        'Sieh deine Steuerlast in Echtzeit'
      ]
    }
  ];

  const currentStepData = steps[currentStep];

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onClose();
      navigate('/dashboard');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-8 animate-in fade-in slide-in-from-bottom-4 duration-300">
        {/* Icon */}
        <div className="flex justify-center mb-6">
          <div className="w-20 h-20 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl flex items-center justify-center shadow-lg">
            <currentStepData.icon className="w-10 h-10 text-white" />
          </div>
        </div>

        {/* Title */}
        <h2 className="text-3xl font-bold text-gray-900 text-center mb-4">
          {currentStepData.title}
        </h2>

        {/* Description */}
        <p className="text-lg text-gray-600 text-center mb-8">
          {currentStepData.description}
        </p>

        {/* Features */}
        <div className="space-y-4 mb-8">
          {currentStepData.features.map((feature, index) => (
            <div 
              key={index}
              className="flex items-start gap-3 p-4 bg-emerald-50 rounded-lg border border-emerald-100"
            >
              <div className="flex-shrink-0 w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center mt-0.5">
                <Check className="w-4 h-4 text-white" />
              </div>
              <p className="text-gray-700 leading-relaxed">{feature}</p>
            </div>
          ))}
        </div>

        {/* Progress Dots */}
        <div className="flex justify-center gap-2 mb-6">
          {steps.map((_, index) => (
            <div
              key={index}
              className={`h-2 rounded-full transition-all ${
                index === currentStep 
                  ? 'w-8 bg-emerald-600' 
                  : 'w-2 bg-gray-300'
              }`}
            />
          ))}
        </div>

        {/* Action Button */}
        <Button
          onClick={handleNext}
          className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-6 text-lg font-semibold"
        >
          {currentStep < steps.length - 1 ? 'Weiter' : 'Zum Dashboard'}
        </Button>

        {/* Skip Link */}
        {currentStep === 0 && (
          <button
            onClick={() => {
              onClose();
              navigate('/dashboard');
            }}
            className="w-full mt-4 text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            Überspringen
          </button>
        )}
      </div>
    </div>
  );
}
